import { ApolloQueryResult } from '@apollo/client';
import Button from '@atlaskit/button';
import InfoIcon from '@atlaskit/icon/glyph/info';
import Tooltip from '@atlaskit/tooltip';
import { BillToPartyDto } from '@atlassian/cssxp-ccp-types';
import { Maybe, TransactionAccount } from '@css-xp/graphtypes';
import { BillingStatus, EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';
import { INVOICE_GROUP_PATH_2, Permissions, TRANSACTION_ACCOUNT_PATH_2 } from '@atlassian/cssxp-typescommon';
import { isDeletedEntitlement } from '@atlassian/cssxp-utils-entitlement';
import {
  ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE,
  ENABLE_SANDBOX_ENTITLEMENT,
  ENABLE_SUBSCRIPTION_BILLING_ACCOUNT_TRANSFER,
  ENABLE_WARNING_MODAL_DAILOG_RELATED_ENTS,
} from '@atlassian/cssxp-utils-generic';
import { isBilledElsewhereProduct, isNpfProduct } from '@atlassian/cssxp-utils-product';
import {
  commonTabs,
  FormattedAddress,
  FormattedDate,
  InfoPanel,
  isAccess,
  OpenSidebarButton,
  OpenSidebarLink,
  OpenSidebarPayload,
  SummaryGridItem,
  SummarySection,
  useFeatureToggle,
  useSidebar,
  useWarningModalDialog,
} from '@css-xp/base';
import { TransactionAccountContext } from '@css-xp/base/common-context';
import { TaxComponent } from '@css-xp/base/tax-exempt/src/tax-component';
import lowerCase from 'lodash/lowerCase';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchWarningProps,
  getSwitchBillingProfilePayload,
  getSwitchTxaPayload,
  SIDEBAR,
  validateFreeSubscriptions,
} from '../../../../entitlement-page';
import { daysForTrialExpiry } from '../../../../entitlement-page-utility/entitlement-page-utils';
import { PREDICATES } from '../../../constants';
import './billing-information.scss';
import './billing-tab.scss';

interface Props {
  entitlement: EntitlementDetails;
  transactionAccount: Maybe<TransactionAccount>;
  refetch?: () => Promise<ApolloQueryResult<unknown>>;
}

export const BillingInformation = ({ entitlement, transactionAccount, refetch }: Props) => {
  const { openSidebar } = useSidebar();
  const { openWarningModalDialog, closeWarningModalDialog } = useWarningModalDialog();
  const transactionAccountContext = React.useContext(TransactionAccountContext);
  const activeBillingGroupId =
    (entitlement as EntitlementDetails)?.activeSubscriptionBillingGroupId ?? entitlement.billingGroupId;

  let invoiceGroup: any = null;
  if (!activeBillingGroupId) {
    invoiceGroup = null;
  } else {
    invoiceGroup = transactionAccount?.availableInvoiceGroups?.nodes.find(
      (invoiceGroup) => activeBillingGroupId === invoiceGroup?.id
    );
  }

  const isNameNone = invoiceGroup?.name && invoiceGroup?.name?.toLowerCase() !== 'none';
  const updatePaymentMethodLink =
    invoiceGroup?.id && (entitlement as EntitlementDetails)?.updatePaymentMethodLink
      ? `${(entitlement as EntitlementDetails)?.updatePaymentMethodLink}${invoiceGroup?.id}/payment-method`
      : null;
  const nextInvoiceTime: any = (entitlement as EntitlementDetails)?.nextInvoiceTimeAt;
  const hasPendingInvoices: any = (entitlement as EntitlementDetails)?.hasPendingInvoices;
  const notProvidedMessage = 'Not provided';

  const isActive = useMemo(() => {
    return entitlement?.billingStatus
      ? lowerCase(entitlement?.billingStatus) === lowerCase(BillingStatus.ACTIVE)
      : false;
  }, [entitlement]);

  //Note: Temporary flag to keep the 'Transfer to another account' link hidden until sidebar is ready
  const [isEnabled] = useFeatureToggle();

  const isSandbox = isEnabled(ENABLE_SANDBOX_ENTITLEMENT) && entitlement?.isSandbox;
  const isWarningModalDialogEnable = isEnabled(ENABLE_WARNING_MODAL_DAILOG_RELATED_ENTS);
  const isEntRelationShipFeatureEnable = isEnabled(ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE);

  const linkedEntsTableVisible =
    isEntRelationShipFeatureEnable &&
    (!!entitlement?.relatesFromEntitlements?.length || !!entitlement?.relatesToEntitlements?.length);

  const warningModalDailogVisible = linkedEntsTableVisible && isWarningModalDialogEnable;

  const billingProfileSidebarPayload = getSwitchBillingProfilePayload(
    entitlement,
    transactionAccountContext,
    refetch as () => void
  );

  const handleConfirmWarningDailog = (sidebarPayload: OpenSidebarPayload) => {
    closeWarningModalDialog();
    openSidebar(sidebarPayload);
  };

  const billingProfileWarningProps = {
    ...fetchWarningProps(PREDICATES.UPDATE_INVOICE_GROUP),
    onConfirm: () =>
      handleConfirmWarningDailog({
        ...billingProfileSidebarPayload,
        sidebarType: billingProfileSidebarPayload.sidebar,
      }),
  };

  const switchTxaPayload = getSwitchTxaPayload({
    entitlement,
    transactionAccount,
    invoiceGroup,
    refetch: refetch as () => void,
  });

  const txaWarningProps = {
    ...fetchWarningProps(PREDICATES.TRANSFER_BILLING_ACCOUNT),
    onConfirm: () =>
      handleConfirmWarningDailog({
        ...switchTxaPayload,
        sidebarType: switchTxaPayload.sidebar,
      }),
  };

  const showBanner = (!invoiceGroup || !invoiceGroup?.defaultPaymentMethod) && daysForTrialExpiry(entitlement) < 4;
  return (
    <div data-testId="billing-information">
      <h4>Billing</h4>
      <div className="billing-information">
        {showBanner && (
          <InfoPanel
            type="warning"
            message={
              "The linked billing profile is incomplete, so this trial can't be converted to a paid subscription"
            }
          />
        )}
        <SummarySection columns="1fr 0.9fr 1fr 1fr 1fr">
          <SummaryGridItem testId="billing-profile-name">
            <label title="Also known as invoice group">Billing profile name</label>
            <div style={{ marginBottom: '16px' }}>
              {invoiceGroup ? (
                <Link to={`${INVOICE_GROUP_PATH_2}/${invoiceGroup.id}`}>
                  {isNameNone ? invoiceGroup.name : 'Unnamed invoice group'}
                </Link>
              ) : (
                notProvidedMessage
              )}
            </div>
            {isActive &&
              !isSandbox &&
              !isDeletedEntitlement(entitlement) &&
              !validateFreeSubscriptions(entitlement as EntitlementDetails) &&
              !isNpfProduct(entitlement?.productId) &&
              !isBilledElsewhereProduct(entitlement?.productId) &&
              isAccess(Permissions.INVOICE_GROUP_WRITE) &&
              (warningModalDailogVisible ? (
                <Button
                  testId="change-billing-profile"
                  aria-label="Switch billing profile"
                  appearance="link"
                  className="billing-profile-switch-desciption"
                  isDisabled={!!entitlement?.hasPendingInvoices}
                  onClick={() => openWarningModalDialog(billingProfileWarningProps)}
                >
                  Switch billing profile
                </Button>
              ) : (
                <OpenSidebarLink
                  {...billingProfileSidebarPayload}
                  appearance="link"
                  spacing="none"
                  isDisabled={!!entitlement?.hasPendingInvoices}
                  label="Switch billing profile"
                >
                  Switch billing profile
                </OpenSidebarLink>
              ))}
            {hasPendingInvoices && (
              <div>
                <label>Next invoice at</label>
                <div>{nextInvoiceTime ? <FormattedDate date={nextInvoiceTime} showTime /> : notProvidedMessage}</div>
              </div>
            )}
          </SummaryGridItem>
          <SummaryGridItem testId="payment-method">
            <label>Payment method</label>
            {invoiceGroup ? (
              <>
                <div style={{ marginBottom: '5px' }}>
                  <commonTabs.PaymentMethodDetails paymentMethod={invoiceGroup.defaultPaymentMethod} />
                </div>
                {!isSandbox && !isDeletedEntitlement(entitlement) && (
                  <div>
                    <span>
                      <OpenSidebarLink
                        sidebar={SIDEBAR.GET_UPDATE_PAYMENT_METHOD_LINK}
                        // TODO: this should be moved to invoice group
                        payload={{
                          updatePaymentMethodLink,
                        }}
                        sidebarOptions={{
                          isOverlay: true,
                          sidebarWidth: '600px',
                        }}
                        label="Get update payment link"
                      >
                        Get update payment link
                      </OpenSidebarLink>
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div>{notProvidedMessage}</div>
            )}
          </SummaryGridItem>
          <SummaryGridItem testId="sold-to-address">
            <label>Sold-to address</label>
            <Tooltip tag="span" content="Sometimes referred to as shipping address">
              <InfoIcon label="info" size="small" />
            </Tooltip>
            <div style={{ marginBottom: '16px' }}>
              {invoiceGroup ? (
                invoiceGroup.shipToParty ? (
                  <FormattedAddress
                    addressLine1={invoiceGroup.shipToParty?.addressLine1}
                    addressLine2={invoiceGroup.shipToParty?.addressLine2}
                    city={invoiceGroup.shipToParty?.city}
                    country={invoiceGroup.shipToParty?.country}
                    postcode={invoiceGroup.shipToParty?.postcode}
                    state={invoiceGroup.shipToParty?.state}
                    organizationName={invoiceGroup.shipToParty?.name}
                  />
                ) : (
                  'Same as billing address'
                )
              ) : (
                'Same as billing address'
              )}
            </div>
            <TaxComponent
              billToAddress={invoiceGroup?.shipToParty}
              transactionAccountNumber={transactionAccount?.transactionAccountNumber}
              shouldRenderTaxExempt={false}
              gridItemClassName="tax-component"
            />
          </SummaryGridItem>
          <SummaryGridItem testId="transaction-account">
            <label>Billing account</label>{' '}
            <Tooltip tag="span" content="Also known as transaction account">
              <InfoIcon label="info" size="small" />
            </Tooltip>
            <div>
              <Link to={`${TRANSACTION_ACCOUNT_PATH_2}/${transactionAccount?.transactionAccountNumber}`}>
                {transactionAccount?.displayName ?? 'Unnamed billing account'}
              </Link>
            </div>
            <div className="txa-account-number-text">{transactionAccount?.transactionAccountNumber}</div>
            {isActive &&
              isEnabled(ENABLE_SUBSCRIPTION_BILLING_ACCOUNT_TRANSFER) &&
              !isSandbox &&
              !isDeletedEntitlement(entitlement) &&
              !isBilledElsewhereProduct(entitlement?.productId) &&
              isAccess(Permissions.SUBSCRIPTION_WRITE) && (
                <>
                  {!entitlement?.hasPendingInvoices ? (
                    <>
                      {warningModalDailogVisible ? (
                        <Button
                          testId="change-billing-account"
                          aria-label="Switch txa"
                          appearance="link"
                          className="txa-switch-desciption"
                          onClick={() => openWarningModalDialog(txaWarningProps)}
                        >
                          Transfer to another account
                        </Button>
                      ) : (
                        <OpenSidebarButton
                          {...switchTxaPayload}
                          appearance="link"
                          spacing="none"
                          label="Transfer to another account"
                        >
                          <div className="mt-8">Transfer to another account</div>
                        </OpenSidebarButton>
                      )}
                    </>
                  ) : (
                    <>
                      <Tooltip
                        content={"An invoice is being generated, so transfers can't be made."}
                        tag="span"
                        position="bottom-end"
                      >
                        <div className="transfer-account-cta mt-8">Transfer to another account</div>
                      </Tooltip>
                    </>
                  )}
                </>
              )}
          </SummaryGridItem>
          <SummaryGridItem testId="billing-address">
            <label>Billing address</label>
            <div style={{ marginBottom: '16px' }}>
              {transactionAccount?.billToParty ? (
                <FormattedAddress
                  addressLine1={transactionAccount?.billToParty?.addressLine1}
                  addressLine2={transactionAccount?.billToParty?.addressLine2}
                  city={transactionAccount?.billToParty?.city}
                  country={transactionAccount?.billToParty?.country}
                  postcode={transactionAccount?.billToParty?.postcode}
                  state={transactionAccount?.billToParty?.state}
                />
              ) : (
                'No address provided'
              )}
            </div>
            <TaxComponent
              billToAddress={transactionAccount?.billToParty as BillToPartyDto}
              transactionAccountNumber={transactionAccount?.transactionAccountNumber}
              shouldRenderTaxExempt={false}
              gridItemClassName="tax-component"
            />
          </SummaryGridItem>
        </SummarySection>
      </div>
    </div>
  );
};
